import { faCommentDots } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { PropsWithChildren, useEffect, useState } from "react";
import {
  FieldPath,
  FieldValues,
  RegisterOptions,
  UseFormReturn,
} from "react-hook-form";
import styled, { css } from "styled-components";

import { LIGHT_BLUE, NEAR_WHITE } from "../../config";
import { translate } from "../../intl";
import { TextField } from "./TextField";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
`;

const FieldWrapper = styled.div<{ showComments: boolean; forceShow: boolean }>`
  display: flex;
  flex-direction: column;

  h5 {
    margin: 0;
    margin-bottom: 8px;
  }

  & > div {
    display: flex;

    .field {
      margin-bottom: 0px;
      align-items: center;
      min-height: 36px;

      &.select-field,
      &.text-area-field {
        flex-grow: 1;
      }
    }
  }

  .fa-comment-dots {
    width: 20px;
    padding: 4px;
    transition: 0.2s ease;
    cursor: pointer;
    margin: 5px;
    font-size: 18px;
    pointer-events: ${({ forceShow }) => (forceShow ? "none" : "unset")};

    ${({ showComments }) =>
      showComments
        ? css`
            color: ${LIGHT_BLUE};
          `
        : css`
            &:hover {
              color: ${NEAR_WHITE};
            }
          `}
  }
`;

const CommentsWrapper = styled.div<{ showComments: boolean }>`
  transition: 0.2s ease;
  overflow: hidden;
  max-height: ${({ showComments }) => (showComments ? "64px" : "0px")};

  .field.text-field {
    padding-top: 8px;
    margin-bottom: 0;
  }
`;

interface FieldWithCommentsProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> {
  form: UseFormReturn<TFieldValues>;
  fieldLabel: string;
  commentsName: TName;
  defaultComments?: string | null;
  commentsPlaceholder?: string;
  children: JSX.Element;
  onToggle?: () => void;
  readOnly?: boolean;
  rules?: Omit<
    RegisterOptions<TFieldValues, TName>,
    "valueAsNumber" | "valueAsDate" | "setValueAs" | "disabled"
  >;
  forceShow?: boolean;
}

export function FieldWithComments<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  form,
  fieldLabel,
  commentsName,
  defaultComments,
  children,
  onToggle,
  commentsPlaceholder,
  readOnly,
  rules,
  forceShow = false,
}: PropsWithChildren<FieldWithCommentsProps<TFieldValues, TName>>) {
  const [showComments, setShowComments] = useState(Boolean(defaultComments));
  const [shouldUnmountComments, setShouldUnmountComments] = useState(
    !showComments,
  );

  const toggleComments = () => {
    if (!showComments) {
      setShouldUnmountComments(false);
    }
    setShowComments(!showComments);
    onToggle?.();
  };

  useEffect(() => {
    if (!showComments) {
      setTimeout(() => {
        setShouldUnmountComments(true);
      }, 200);
    }
  }, [showComments]);

  useEffect(() => {
    if (forceShow) {
      setShouldUnmountComments(false);
      setShowComments(true);
    }
  }, [forceShow]);

  return (
    <Container className="field field-with-comments">
      <FieldWrapper showComments={showComments} forceShow={forceShow}>
        {fieldLabel ? <h5>{fieldLabel}</h5> : null}
        <div>
          {children}
          {!readOnly ? (
            <FontAwesomeIcon icon={faCommentDots} onClick={toggleComments} />
          ) : null}
        </div>
      </FieldWrapper>
      <CommentsWrapper showComments={showComments}>
        {!shouldUnmountComments ? (
          <TextField
            form={form}
            name={commentsName}
            placeholder={commentsPlaceholder ?? translate("form.fieldWith")}
            defaultValue={defaultComments}
            readOnly={readOnly}
            rules={rules}
          />
        ) : null}
      </CommentsWrapper>
    </Container>
  );
}

FieldWithComments.defaultProps = {
  defaultComments: "",
  readOnly: false,
};
